import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import api from '../utils/Api';
import { trackEvent } from '../utils/Analytics';
import { SHOUTOUT_MAX_LENGTH, ShoutoutMode, ShoutoutOffer, ShoutoutStatus, countCharacters } from '../utils/Shoutouts';
import { formatPrice } from './BuyBumps';

import './ShoutoutCard.scss';

interface ShoutoutCardProps {
  token: string;
  playerIdentifier: string;
  /** The player's setting, so the card comes and goes as the owner changes it. */
  mode: ShoutoutMode | undefined;
  /** The page Stripe sends them back to, such as /jukebox/abc123. */
  returnPath: string;
  onNotice: (tone: 'ok' | 'bad', text: string) => void;
}

const STATUS_TEXT: { [status: string]: string } = {
  pending_payment: 'Payment received. Getting your shout-out ready…',
  approved: 'Your shout-out is on the TV.',
  shown: 'Your shout-out has been on the TV.',
  pending_review: 'Waiting for the venue to approve it.',
  rejected: 'The venue turned this one down. You have been refunded in full.',
};

/**
 * A paid shout-out on the jukebox page: a short message for the venue's TV ticker. The server
 * checks the message before Stripe is asked for anything, so a refused message is never paid
 * for, and puts it on the TV once Stripe confirms the payment. Coming back here only asks how
 * it is doing.
 *
 * Shows nothing when the venue has shout-outs off or the server has no Stripe keys.
 */
const ShoutoutCard = ({ token, playerIdentifier, mode, returnPath, onNotice }: ShoutoutCardProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [offer, setOffer] = useState<ShoutoutOffer>(null);
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>(null);
  const [status, setStatus] = useState<ShoutoutStatus>(null);
  // Set when this account is banned from shout-outs here or everywhere.
  const [blocked, setBlocked] = useState<string>(null);

  useEffect(() => {
    if (!mode || mode === 'off') {
      setOffer(null);
      return;
    }
    let live = true;
    api
      .shoutouts(playerIdentifier, 'GET', token, null)
      .then(response => {
        if (!live) return;
        setOffer((response && response.offer) || null);
        setBlocked((response && response.blocked) || null);
      })
      .catch(() => live && setOffer(null));
    return () => {
      live = false;
    };
  }, [mode, playerIdentifier, token]);

  // Back from Stripe. The webhook can land a moment after they do, so it asks a few times.
  // The flags come off the address so a reload does not say it again.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const outcome = params.get('shoutout');
    if (outcome !== 'paid' && outcome !== 'cancelled') {
      return;
    }
    const session = params.get('session') || '';
    navigate(location.pathname, { replace: true });
    if (outcome === 'cancelled') {
      trackEvent('shoutout_checkout_cancelled', { player_code: playerIdentifier });
      onNotice('bad', 'Shout-out cancelled. Nothing was charged.');
      return;
    }
    setStatus('pending_payment');
    trackEvent('shoutout_paid', { player_code: playerIdentifier });
    if (!/^cs_[A-Za-z0-9_]+$/.test(session)) {
      return;
    }
    let tries = 0;
    let timer: number = null;
    const check = () => {
      tries += 1;
      api
        .shoutouts(`${playerIdentifier}/mine/${session}`, 'GET', token, null)
        .then(response => {
          const next: ShoutoutStatus = response && response.status;
          if (next) setStatus(next);
          if (next === 'pending_payment' && tries < 10) timer = window.setTimeout(check, 2000);
        })
        .catch(() => {
          if (tries < 10) timer = window.setTimeout(check, 2000);
        });
    };
    check();
    return () => window.clearTimeout(timer);
  }, [location.search]);

  if (!offer && !status) {
    return null;
  }

  const length = countCharacters(message.trim());
  const maxLength = (offer && offer.maxLength) || SHOUTOUT_MAX_LENGTH;
  const tooLong = length > maxLength;
  const price = offer ? formatPrice(offer.amount, offer.currency) : '';

  const pay = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || tooLong || busy) return;
    setBusy(true);
    setError(null);
    try {
      const { url } = await api.shoutouts(`${playerIdentifier}/checkout`, 'POST', token, { message, returnPath });
      trackEvent('shoutout_checkout_started', { player_code: playerIdentifier });
      window.location.assign(url);
    } catch (err) {
      // The server's refusals are written for a patron: too long, a link, a word it won't show.
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="shoutout">
      {!!status && (
        <div className={`shoutout-status shoutout-status-${status}`} role="status">
          <span className="shoutout-status-badge" aria-hidden="true">
            📣
          </span>
          {STATUS_TEXT[status] || STATUS_TEXT.pending_payment}
        </div>
      )}

      {!!offer && !!blocked && <div className="shoutout-small shoutout-blocked">{blocked}</div>}

      {!!offer && !blocked && !open && (
        <button type="button" className="shoutout-toggle" onClick={() => setOpen(true)}>
          <span aria-hidden="true">📣</span> Shout-out on the TV
        </button>
      )}

      {!!offer && !blocked && open && (
        <form className="shoutout-card" onSubmit={pay}>
          <div className="shoutout-head">
            <span className="shoutout-title">
              <span aria-hidden="true">📣</span> Shout-out on the TV
            </span>
            <button type="button" className="shoutout-close" onClick={() => setOpen(false)}>
              Close
            </button>
          </div>
          <p className="shoutout-help">
            {offer.popupSeconds
              ? `Your message pops up big on the TV for ${offer.popupSeconds} seconds, then scrolls across it for ${offer.minutes} minutes, with your username.`
              : `Your message scrolls across the TV for ${offer.minutes} minutes with your username.`}
            {offer.review ? ' The venue approves it first.' : ''}
          </p>
          <textarea
            className="shoutout-input"
            value={message}
            rows={2}
            placeholder="Happy birthday, Sam! 🎉"
            aria-label="Your shout-out"
            onChange={e => {
              setMessage(e.target.value);
              setError(null);
            }}
          />
          <div className="shoutout-meta">
            <span className={tooLong ? 'shoutout-count is-over' : 'shoutout-count'}>
              {length}/{maxLength}
            </span>
            <span>No links. Keep it friendly.</span>
          </div>
          {!!error && <div className="shoutout-error">{error}</div>}
          <button type="submit" className="shoutout-pay" disabled={busy || !message.trim() || tooLong}>
            {busy ? 'Opening' : `Pay ${price}`}
          </button>
          <div className="shoutout-small">
            Payment is handled by Stripe.{offer.review ? ' Refunded in full if the venue turns it down.' : ''}
          </div>
        </form>
      )}
    </div>
  );
};

export default ShoutoutCard;
